import { useCurrentMarketLocation, useForecastDataSync, useLocale } from '@e2m-customer-portal/hooks';
import { convertMeasure } from '@e2m-customer-portal/shared';
import { DowntimeButton, DowntimeRevokeModal, ErrorComponent } from '@e2m-customer-portal/ui';
import { Box, LinearProgress } from '@mui/material';
import clsx from 'clsx';
import { addDays, addHours, eachDayOfInterval, getISOWeek, Interval, toDate } from 'date-fns';
import { DeviceWithInstalledPower } from 'domains/forecast/control-energy-forecast/ControlEnergyForecast';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getDisplayName } from '../../../../utils';
import { OutageSlice } from './OutageSlice';

const DAY_WIDTH = '3.5rem';

export interface OutagesDeviceListProps {
  devices: readonly DeviceWithInstalledPower[];
  selectedDevices: readonly DeviceWithInstalledPower[];
  interval: Interval;
  editable: boolean;
}

export function OutagesDeviceList({
  devices,
  selectedDevices,
  interval,
  editable,
}: Readonly<OutagesDeviceListProps>): ReactElement<OutagesDeviceListProps> {
  const [t] = useTranslation();
  const { code: locale } = useLocale();
  const [legendStatus, setLegendStatus] = useState({ showAvailable: true, showOutage: true });
  const marketLocation = useCurrentMarketLocation();
  const {
    loading,
    data: forecasts,
    error,
  } = useForecastDataSync(
    devices.map((device) => device.id),
    { start: interval.start, end: addDays(interval.end, 1) },
  );

  if (error) {
    return <ErrorComponent error={error} />;
  }

  const dates = eachDayOfInterval(interval);
  const months: Array<{ calendarMonth: number; length: number }> = [];
  const weeks: Array<{ calendarWeek: number; length: number }> = [];
  for (const date of dates) {
    const month = months.find((m) => m.calendarMonth === date.getMonth());
    if (month?.length) {
      month.length += 1;
    } else {
      months.push({ calendarMonth: date.getMonth(), length: 1 });
    }

    const week = weeks.find((w) => w.calendarWeek === getISOWeek(date));
    if (week?.length) {
      week.length += 1;
    } else {
      weeks.push({ calendarWeek: getISOWeek(date), length: 1 });
    }
  }

  const [revokeDowntimeInterval, setRevokeDowntimeInterval] = useState<Interval | null>(null);
  const [runningRevocationDate, setRunningRevocationDate] = useState<string | number | Date | null>(null);

  return (
    <>
      {selectedDevices.map((device) => {
        const forecast = forecasts?.find((f) => f.deviceId === device.id);
        const installedPower = device.installedPower && convertMeasure(device.installedPower, 'kW');
        const outages: Array<{ interval: Interval; lengthInHours: number; isOutage: boolean }> = [];
        if (forecast) {
          for (const slice of forecast.slices) {
            const outage = outages.findLast((o) => o.interval.start < slice.date);
            if (outage?.interval && outage.isOutage === slice.hasDowntime) {
              outage.interval.end = addHours(slice.date, 1);
              outage.lengthInHours += 1;
            } else {
              outages.push({
                interval: { start: slice.date, end: addHours(slice.date, 1) },
                lengthInHours: 1,
                isOutage: slice.hasDowntime,
              });
            }
          }
        }

        return (
          <div key={device.id} data-testid="outage-device-list">
            <DowntimeRevokeModal
              device={device}
              revokeDowntimeInterval={revokeDowntimeInterval}
              setRevokeDowntimeInterval={setRevokeDowntimeInterval}
              onRevocation={(date) => date && setRunningRevocationDate(date)}
            />
            <div className="flex items-center justify-between shadow-[0px_2px_4px] shadow-black/25">
              <div className="flex">
                <div className="mb-2 ml-6 mt-3 flex flex-col gap-x-3 gap-y-1">
                  <div className="text-xs leading-3 text-grey-400">
                    {device.externalName ? device.name : t('DEVICE_TABLE_HEADER_TECHNICAL_UNIT')}
                  </div>
                  <div className="text-xl leading-6">{getDisplayName(device)}</div>
                </div>
                {installedPower && (
                  <div className="flex w-max items-center ml-4 border-0 border-l border-divider border-solid">
                    <div className="ml-2 flex flex-col whitespace-nowrap">
                      <span className="text-xs  text-grey-400">{t('DEVICE_TABLE_HEADER_INSTALLED_POWER_LABEL')}</span>
                      <div className="sm:text-xl">
                        <div className="relative flex items-center">
                          {t('DEVICE_TABLE_HEADER_INSTALLED_POWER', { value: installedPower.value })}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="mx-8 my-3 flex items-center gap-x-8" data-testid="outage-filter">
                <div className="flex items-center gap-x-4 text-sm leading-6">
                  <button
                    type="button"
                    className={clsx(
                      'flex h-8 items-center gap-x-1 rounded-xl border border-solid border-green-200 bg-transparent px-2.5 transition-all hover:bg-green-200/5',
                      { 'opacity-50': !legendStatus.showAvailable },
                    )}
                    onClick={() => setLegendStatus({ ...legendStatus, showAvailable: !legendStatus.showAvailable })}
                  >
                    <div className="h-2 w-4 bg-[repeating-linear-gradient(90deg,_var(--tw-gradient-from)_0_9px,_var(--tw-gradient-to)_9px_12px)] from-green-200/50 to-transparent" />
                    <div className="hidden sm:block">{t('MARKET_LOCATION_AVAILABILITY_FILTER')}</div>
                  </button>
                  <button
                    type="button"
                    className={clsx(
                      'flex h-8 items-center gap-x-1 rounded-xl border border-solid border-green-200 bg-transparent px-2.5 transition-all hover:bg-green-200/5',
                      { 'opacity-50': !legendStatus.showOutage },
                    )}
                    onClick={() => setLegendStatus({ ...legendStatus, showOutage: !legendStatus.showOutage })}
                  >
                    <div className="h-2 w-4 bg-[repeating-linear-gradient(90deg,_var(--tw-gradient-from)_0_9px,_var(--tw-gradient-to)_9px_12px)] from-[#DA2317]/50 to-transparent" />
                    <div className="hidden sm:block">{t('MARKET_LOCATION_OUTAGE_FILTER')}</div>
                  </button>
                </div>
                {editable && <DowntimeButton marketLocation={marketLocation} device={device} />}
              </div>
            </div>
            <div
              className="relative h-[10.5rem] overflow-x-scroll overflow-y-hidden"
              aria-busy={loading}
              data-testid="outage-table"
            >
              <div className="absolute w-fit">
                <div
                  className="flex border-0 border-b border-solid border-b-grey-200 pl-4 text-sm font-medium leading-4"
                  aria-hidden
                >
                  {months.map(({ calendarMonth, length }) => (
                    <Box
                      key={`month-${calendarMonth}`}
                      className="box-border p-2.5 shadow-[inset_-1px_0px_0px] shadow-black/[12%] last:shadow-none"
                      sx={{ minWidth: `calc(${length} * ${DAY_WIDTH})`, width: `calc(${length} * 100vw / 28)` }}
                    >
                      {new Date(new Date().setMonth(calendarMonth)).toLocaleDateString(locale, { month: 'long' })}
                    </Box>
                  ))}
                </div>
                <div
                  className="flex border-0 border-b border-solid border-b-grey-200 bg-grey-50 pl-4 text-xs font-medium leading-3"
                  aria-hidden
                >
                  {weeks.map(({ calendarWeek, length }) => (
                    <Box
                      key={`week-${calendarWeek}`}
                      data-testid="outage-calendar-week"
                      className="box-border p-2.5 pt-4 shadow-[inset_-1px_0px_0px] shadow-grey-200 last:shadow-none"
                      sx={{ minWidth: `calc(${length} * ${DAY_WIDTH})`, width: `calc(${length} * 100vw / 28)` }}
                    >
                      {t('DATE_NAVIGATOR_SINGLE_CALENDAR_WEEK', { calendarWeek })}
                    </Box>
                  ))}
                </div>
                <div
                  className="flex justify-end border-0 border-b border-solid border-b-grey-200 bg-grey-50 text-xs leading-3"
                  aria-hidden
                >
                  {dates.map((date: Date) => (
                    <Box
                      key={`day-${date.toISOString()}`}
                      data-testid="outage-calendar-day"
                      className={clsx(
                        'box-border flex w-[calc(100vw/28)] flex-col justify-center pl-2.5 shadow-[inset_-1px_0px_0px] shadow-grey-200 first:grow first:pl-[1.625rem] last:shadow-none',
                        { 'bg-blue-grey-200/5': !((date.getDay() + 6) % 6) },
                      )}
                      sx={{
                        minWidth: `calc(${DAY_WIDTH})`,
                        height: `calc(${DAY_WIDTH} / 1.5)`,
                      }}
                    >
                      <div>
                        <div>{date.toLocaleDateString(locale, { weekday: 'short' })}</div>
                        <div>{date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' })}</div>
                      </div>
                    </Box>
                  ))}
                </div>
                <div className="relative">
                  <div className="flex h-[calc(2.5rem-1px)] justify-end">
                    {dates.map((date: Date) => (
                      <Box
                        key={`sheet-${date.toISOString()}`}
                        className={clsx(
                          'box-border w-[calc(100vw/28)] border-0 border-b border-solid border-b-grey-200 pl-2.5 shadow-[inset_-1px_0px_0px] shadow-grey-200 first:grow first:pl-[1.625rem] last:shadow-none',
                          { 'bg-blue-grey-200/5': !((date.getDay() + 6) % 6) },
                        )}
                        sx={{
                          minWidth: `calc(${DAY_WIDTH})`,
                        }}
                      />
                    ))}
                  </div>
                  <div className="absolute top-[calc(0.25rem-1px)] flex h-8 justify-end">
                    {loading && (
                      <LinearProgress
                        className="my-3 h-2 w-screen opacity-50"
                        sx={{
                          minWidth: `calc(28 * ${DAY_WIDTH})`,
                        }}
                        variant="query"
                      />
                    )}
                    {outages.map((outage) => (
                      <OutageSlice
                        key={`outage-${toDate(outage.interval.start).toISOString()}`}
                        interval={outage.interval}
                        lengthInHours={outage.lengthInHours}
                        isOutage={outage.isOutage}
                        dayWidth={DAY_WIDTH}
                        legendStatus={legendStatus}
                        isBeingRevoked={outage.interval.start === runningRevocationDate}
                        setRevokeDowntimeInterval={setRevokeDowntimeInterval}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
}
